/**
 * "Saved drafts" — the come-back-after-a-tab-close hub. Lists the signed-in
 * user's in-progress project drafts (chat or form) with their AI name + a few
 * hints; Resume reopens the right screen with the conversation/form restored,
 * the title is inline-editable (a click on the pencil), and Discard drops it.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, MessageSquareText, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useDeleteProjectDraft,
  useProjectDrafts,
  useRenameProjectDraft,
} from '@/api/project-drafts';
import { resumeDraft } from '@/lib/resumeDraft';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { formatRelative } from '@/lib/format';

const KIND_LABEL: Record<string, string> = {
  install: 'Install',
  reconfigure: 'Reconfigure',
  punch: 'Punch',
  service: 'Service',
  pickup: 'Pickup',
  other: 'Project',
};

export function SavedDraftsCard() {
  const { data } = useProjectDrafts();
  const del = useDeleteProjectDraft();
  const rename = useRenameProjectDraft();
  const navigate = useNavigate();
  const [resumingId, setResumingId] = useState<string | null>(null);

  // Inline rename state.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const cancelNextBlur = useRef(false);

  const drafts = data?.data ?? [];
  if (drafts.length === 0) return null;

  async function resume(id: string) {
    setResumingId(id);
    try {
      const { route, hadFiles } = await resumeDraft(id);
      if (hadFiles) {
        toast.info('Re-attach any documents to include them on the project.');
      }
      navigate(route);
    } catch (e) {
      toast.error(`Couldn't open that draft: ${(e as Error).message}`);
      setResumingId(null);
    }
  }

  function startRename(id: string, current: string | null) {
    setEditingId(id);
    setEditValue(current ?? '');
  }

  function commitRename(id: string, original: string | null) {
    const next = editValue.trim();
    setEditingId(null);
    if (next && next !== (original ?? '')) {
      rename.mutate({ id, title: next });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Saved drafts</CardTitle>
        <p className="text-xs text-ink-faint">
          Pick up a request you started earlier — saved automatically as you go.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {drafts.map((d) => (
          <div
            key={d.id}
            className="flex items-center gap-3 rounded-lg border border-line px-3 py-2"
          >
            <div className="shrink-0 text-ink-faint">
              {d.source === 'chat' ? (
                <MessageSquareText className="w-4 h-4" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              {editingId === d.id ? (
                <input
                  autoFocus
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') {
                      cancelNextBlur.current = true;
                      e.currentTarget.blur();
                    }
                  }}
                  onBlur={() => {
                    if (cancelNextBlur.current) {
                      cancelNextBlur.current = false;
                      setEditingId(null);
                      return;
                    }
                    commitRename(d.id, d.title);
                  }}
                  maxLength={120}
                  className="w-full rounded-md border border-accent bg-bg px-1.5 py-0.5 text-sm font-medium focus:outline-none"
                  aria-label="Draft name"
                />
              ) : (
                <div className="flex items-center gap-1">
                  <span className="font-medium truncate">{d.title ?? 'Untitled draft'}</span>
                  <button
                    type="button"
                    onClick={() => startRename(d.id, d.title)}
                    aria-label="Rename draft"
                    title="Rename"
                    className="shrink-0 text-ink-faint/60 hover:text-ink transition-colors"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="text-xs text-ink-faint flex flex-wrap items-center gap-x-1.5">
                <span>{d.source === 'chat' ? 'Chat' : 'Form'}</span>
                {d.hints.kind && <span>· {KIND_LABEL[d.hints.kind] ?? d.hints.kind}</span>}
                {d.hints.client && <span className="truncate">· {d.hints.client}</span>}
                <span>· edited {formatRelative(d.updated_at)}</span>
                {d.hints.built && (
                  <span className="px-1.5 rounded-full bg-status-complete text-status-complete-fg text-[10px] font-semibold uppercase tracking-wide">
                    Built
                  </span>
                )}
              </div>
            </div>
            <Button size="sm" onClick={() => void resume(d.id)} disabled={resumingId === d.id}>
              {resumingId === d.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Resume
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label="Discard draft"
              title="Discard draft"
              onClick={() => del.mutate(d.id)}
              disabled={del.isPending}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
